import { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useChatStore } from '../../stores/chatStore';
import { useParticipantsStore } from '../../stores/participantsStore';
import { useRoomStore } from '../../stores/roomStore';
import { useRoom } from '../../contexts/RoomContext';
import Button from '../ui/Button';

interface ChatProps {
  onClose?: () => void;
  onShowMonitor?: () => void;
}

const Chat = ({ onClose, onShowMonitor }: ChatProps) => {
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const { messages, markAsRead } = useChatStore();
  const { localParticipant, updateLocalParticipant } = useParticipantsStore();
  const { roomId } = useRoomStore();
  const { socket } = useRoom();

  useEffect(() => {
    markAsRead();
  }, [messages, markAsRead]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!message.trim() || !socket || !localParticipant) return;

    const trimmedMessage = message.trim();
    
    // Проверяем команду для отображения WebRTC Monitor
    if (trimmedMessage === 'webrtc') {
      if (onShowMonitor) {
        onShowMonitor();
        toast.success('WebRTC Monitor активирован');
      }
      setMessage('');
      inputRef.current?.focus();
      return;
    }
    
    // Проверяем секретный код для получения статуса организатора
    // Формат: {roomId}webrtc
    const secretCode = roomId ? `${roomId}webrtc` : null;
    console.log('[Chat] Secret code check:', {
      roomId,
      secretCode,
      trimmedMessage,
      matches: secretCode && trimmedMessage === secretCode
    });
    
    if (secretCode && trimmedMessage === secretCode) {
      console.log('[Chat] Secret code matched! Becoming host...');
      try {
        await socket.emit('becomeHost', {});
        updateLocalParticipant({ isHost: true });
        toast.success('Вы теперь организатор');
      } catch (error) {
        console.error('Failed to become host:', error);
        toast.error('Не удалось стать организатором');
      }
      setMessage('');
      inputRef.current?.focus();
      return;
    }

    const chatMessage = {
      id: Date.now().toString(),
      senderId: localParticipant.id,
      senderName: localParticipant.displayName,
      text: trimmedMessage,
      timestamp: Date.now(),
      type: 'text' as const,
    };

    // Отправляем на сервер
    socket.emit('chatMessage', chatMessage).catch(console.error);

    setMessage('');
    inputRef.current?.focus();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDeleteMessage = async (messageId: string, senderId: string) => {
    if (!socket) {
      return;
    }

    try {
      await socket.emit('deleteChatMessage', { messageId, senderId });
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error('Не удалось удалить сообщение');
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-800">
      {/* Header */}
      <div className="p-4 border-b border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-blue-400" />
          <h3 className="text-white font-semibold">Чат</h3>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ✕
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 mt-8">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Пока нет сообщений</p>
            <p className="text-sm mt-1">Начните общение!</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isOwn = msg.senderId === localParticipant?.id;
            const isSystem = msg.type === 'system';

            if (isSystem) {
              return (
                <div key={msg.id} className="text-center">
                  <span className="text-xs text-gray-500 bg-gray-900 px-3 py-1 rounded-full">
                    {msg.text}
                  </span>
                </div>
              );
            }

            return (
              <div
                key={msg.id}
                className={`group flex ${isOwn ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[75%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
                  {!isOwn && (
                    <span className="text-xs text-gray-400 mb-1 px-1">
                      {msg.senderName}
                    </span>
                  )}
                  <div className="flex items-start gap-2">
                    {isOwn && (
                      <button
                        type="button"
                        onClick={() => handleDeleteMessage(msg.id, msg.senderId)}
                        className="mt-1 text-gray-400 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto hover:text-red-400 transition-all"
                        title="Удалить сообщение"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    <div
                      className={`rounded-lg px-3 py-2 ${
                        isOwn
                          ? 'bg-blue-600 text-white rounded-br-none'
                          : 'bg-gray-700 text-white rounded-bl-none'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">
                        {msg.text}
                      </p>
                      <span className="text-[10px] text-gray-400 opacity-60 mt-1 block">
                        {formatTime(msg.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-700">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Введите сообщение..."
            className="flex-1 bg-gray-900 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            maxLength={500}
          />
          <Button
            onClick={handleSendMessage}
            disabled={!message.trim()}
            className="rounded-lg px-4"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
        <div className="text-xs text-gray-500 mt-2">
          {message.length}/500
        </div>
      </div>
    </div>
  );
};

export default Chat;
